import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, Search, RefreshCw, Eye, X, Clock, CheckCircle, 
  Truck, Activity, FileText, User, Phone, Barcode, Calendar,
  ChevronLeft, ChevronRight, Loader2, AlertCircle, ExternalLink
} from 'lucide-react';
import { contractorLabsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const ContractorBookingsList = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const ITEMS_PER_PAGE = 20;

  // Modals
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        page: currentPage, 
        limit: ITEMS_PER_PAGE 
      };
      if (statusFilter) params.status = statusFilter;
      
      const response = await contractorLabsAPI.getAllBookings(params);
      const data = response.data?.data;
      setBookings(data?.items || []);
      setTotalItems(data?.total || 0);
      setTotalPages(Math.ceil((data?.total || 0) / ITEMS_PER_PAGE));
    } catch (err) {
      console.error('Error fetching bookings:', err);
      toast.error('حدث خطأ في تحميل البيانات');
    }
    setLoading(false);
  }, [currentPage, statusFilter]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleStatusChange = async (bookingId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await contractorLabsAPI.updateBookingStatus(bookingId, { status: newStatus });
      toast.success('تم تحديث الحالة بنجاح');
      fetchBookings();
      if (selectedBooking?._id === bookingId) {
        setSelectedBooking(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ أثناء تحديث الحالة');
    }
    setUpdatingStatus(false);
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { label: 'قيد الانتظار', color: 'bg-amber-100 text-amber-700', icon: Clock },
      collected: { label: 'تم الجمع', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      in_transit: { label: 'في التوصيل', color: 'bg-purple-100 text-purple-700', icon: Truck },
      processing: { label: 'قيد المعالجة', color: 'bg-cyan-100 text-cyan-700', icon: Activity },
      completed: { label: 'مكتمل', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-700', icon: X }
    };
    return statusMap[status] || statusMap.pending;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ar-SA-u-ca-gregory', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredBookings = bookings.filter(b => 
    b.patient_name?.includes(searchQuery) ||
    b.patient_national_id?.includes(searchQuery) ||
    b.booking_no?.includes(searchQuery) ||
    b.contractor_lab_id?.name?.includes(searchQuery)
  );

  const statusOptions = [
    { value: 'pending', label: 'قيد الانتظار' },
    { value: 'collected', label: 'تم الجمع' },
    { value: 'in_transit', label: 'في التوصيل' },
    { value: 'processing', label: 'قيد المعالجة' },
    { value: 'completed', label: 'مكتمل' },
    { value: 'cancelled', label: 'ملغي' }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">حجوزات المختبرات المتعاقدة</h1>
              <p className="text-slate-500 text-sm mt-1">
                إجمالي <span className="font-semibold text-slate-700">{totalItems}</span> طلب
              </p>
            </div>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="البحث برقم الطلب أو اسم المريض أو المختبر..."
                className="w-full px-4 py-2.5 pr-11 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white"
              />
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500"
            >
              <option value="">جميع الحالات</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button 
              onClick={fetchBookings} 
              className="flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:bg-gray-50 rounded-xl border border-gray-200"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Table */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_4px_6px_-1px_rgb(0_0_0_/_0.05)] overflow-hidden"
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-3" />
              <p className="text-sm text-slate-500">جاري تحميل الطلبات...</p>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-slate-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">لا توجد طلبات</h3>
              <p className="text-sm text-slate-500">لم يتم العثور على طلبات من المختبرات المتعاقدة</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">رقم الطلب</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">المختبر</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">المريض</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">التحاليل</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">المبلغ</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">التاريخ</th>
                      <th className="px-6 py-4 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-4 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((booking, index) => {
                      const status = getStatusInfo(booking.status);
                      const StatusIcon = status.icon;
                      
                      return (
                        <motion.tr
                          key={booking._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.02 }}
                          className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors duration-150"
                        >
                          <td className="px-6 py-5">
                            <span className="font-mono font-bold text-slate-900 text-sm">#{booking.booking_no}</span>
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-slate-800 text-sm">{booking.contractor_lab_id?.name || '-'}</p>
                                <p className="text-xs text-slate-500">{booking.contractor_lab_id?.city}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <div>
                              <p className="font-medium text-slate-800 text-sm">{booking.patient_name}</p>
                              <p className="text-xs text-slate-500" dir="ltr">{booking.patient_national_id}</p>
                            </div>
                          </td>
                          <td className="px-6 py-5">
                            <span className="text-sm text-slate-600">{booking.tests?.length || 0} تحليل</span>
                          </td>
                          <td className="px-6 py-5">
                            <span className="font-semibold text-slate-800">{booking.total_amount?.toFixed(2)} ر.س</span>
                          </td>
                          <td className="px-6 py-5">
                            <span className="text-sm text-slate-500">{formatDate(booking.created_at)}</span>
                          </td>
                          <td className="px-6 py-5">
                            <select
                              value={booking.status}
                              onChange={(e) => handleStatusChange(booking._id, e.target.value)}
                              disabled={updatingStatus}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer ${status.color}`}
                            >
                              {statusOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-5 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {booking.internal_booking_id ? (
                                <button
                                  onClick={() => navigate(`/bookings/${booking.internal_booking_id._id || booking.internal_booking_id}`)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm font-medium"
                                  title="فتح صفحة تفاصيل الحجز"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  تفاصيل الحجز
                                </button>
                              ) : (
                                <button
                                  onClick={() => { setSelectedBooking(booking); setShowViewModal(true); }}
                                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                  title="عرض التفاصيل"
                                >
                                  <Eye className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-sm text-slate-500">
                    عرض {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} من {totalItems}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <span className="px-3 py-1 text-sm text-slate-600">{currentPage} / {totalPages}</span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* View Modal */}
      <AnimatePresence>
        {showViewModal && selectedBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 bg-[#1E293B]">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white">
                    تفاصيل الطلب #{selectedBooking.booking_no}
                  </h2>
                  <button onClick={() => setShowViewModal(false)} className="text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto flex-1 space-y-6">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">الحالة:</span>
                  <select
                    value={selectedBooking.status}
                    onChange={(e) => handleStatusChange(selectedBooking._id, e.target.value)}
                    disabled={updatingStatus}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border-0 cursor-pointer ${getStatusInfo(selectedBooking.status).color}`}
                  >
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Contractor Lab Info */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    المختبر المتعاقد
                  </h4>
                  <p className="font-medium text-slate-800">{selectedBooking.contractor_lab_id?.name}</p>
                  <p className="text-sm text-slate-600">{selectedBooking.contractor_lab_id?.city}</p>
                </div>

                {/* Patient Info */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <User className="w-5 h-5 text-slate-600" />
                    بيانات المريض
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">الاسم:</span>
                      <p className="font-medium text-slate-800">{selectedBooking.patient_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">رقم الهوية:</span>
                      <p className="font-medium text-slate-800" dir="ltr">{selectedBooking.patient_national_id}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">الجنس:</span>
                      <p className="font-medium text-slate-800">{selectedBooking.patient_gender === 'Male' ? 'ذكر' : 'أنثى'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">رقم الجوال:</span>
                      <p className="font-medium text-slate-800" dir="ltr">{selectedBooking.patient_phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Tests with Barcodes */}
                <div>
                  <h4 className="font-semibold text-slate-800 mb-3">التحاليل المطلوبة</h4>
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">التحليل</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">الباركود</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500">السعر</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBooking.tests?.map((test, idx) => (
                          <tr key={idx} className="border-b border-gray-100">
                            <td className="px-4 py-3">
                              <p className="font-medium text-slate-800">{test.lab_test_id?.name_en || '-'}</p>
                              {test.lab_test_id?.specimen_type && (
                                <p className="text-xs text-slate-500">{test.lab_test_id.specimen_type}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Barcode className="w-4 h-4 text-slate-400" />
                                <span className="font-mono text-sm text-slate-800">{test.barcode}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-800">{test.price?.toFixed(2)} ر.س</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-blue-50">
                          <td colSpan={2} className="px-4 py-3 font-semibold text-slate-800">الإجمالي</td>
                          <td className="px-4 py-3 font-bold text-blue-600">{selectedBooking.total_amount?.toFixed(2)} ر.س</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Notes */}
                {selectedBooking.notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <h4 className="font-semibold text-amber-800 mb-2">ملاحظات</h4>
                    <p className="text-amber-700">{selectedBooking.notes}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-5 py-2.5 bg-slate-600 text-white rounded-xl hover:bg-slate-700"
                >
                  إغلاق
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContractorBookingsList;
